import React from 'react';

import { CheckCircle, ExternalLink } from 'lucide-react';

import {
  InvoiceStatusConstants,
  invoiceStatusLabel,
} from '../../constants/InvoiceStatusConstants';
import { InvoiceDTO } from '../../services/apiClient';
import { convertToUserTime } from '../../utils/dateUtils/convertToUserTime';
import Table from '../common/Table';

interface InvoiceTableProps {
  data: InvoiceDTO[];
  handleMarkAsPaid: (row: InvoiceDTO) => void;
  currentPage: number;
  totalPages: number;
  setCurrentPage: (page: number) => void;
  pageSize: number;
  setPageSize: (size: number) => void;
  isLoading: boolean;
}

const statusBadgeClass = (statusId?: string) => {
  if (statusId === InvoiceStatusConstants.PAID_ID) {
    return 'bg-green-100 text-green-800';
  }
  if (statusId === InvoiceStatusConstants.CANCELLED_ID) {
    return 'bg-red-100 text-red-800';
  }
  return 'bg-yellow-100 text-yellow-800';
};

// Renewal invoices are created (already paid) straight from the Stripe recurring-billing webhook -
// see InvoiceService.CreateRenewalInvoiceAsync - and are the only ones stamped with this Notes
// value, so it doubles as a cheap way to tell "first payment" and "renewal" apart in the list.
const isRenewal = (row: InvoiceDTO) => row.notes === 'Subscription renewal';

const InvoiceTable: React.FC<InvoiceTableProps> = ({
  data,
  handleMarkAsPaid,
  currentPage,
  totalPages,
  setCurrentPage,
  pageSize,
  setPageSize,
  isLoading,
}) => (
  <Table
    columns={[
      { label: 'Invoice Number', key: 'invoiceNumber' },
      { label: 'Member', key: 'memberName' },
      { label: 'Email', key: 'memberEmail' },
      {
        label: 'Type',
        key: 'notes',
        render: (row: InvoiceDTO) => (
          <span
            className={`px-2 py-1 rounded text-xs font-semibold ${
              isRenewal(row)
                ? 'bg-blue-100 text-blue-800'
                : 'bg-gray-100 text-gray-700'
            }`}
          >
            {isRenewal(row) ? 'Renewal' : 'Signup'}
          </span>
        ),
      },
      {
        label: 'Status',
        key: 'invoiceStatusId',
        render: (row: InvoiceDTO) => (
          <span
            className={`px-2 py-1 rounded text-xs font-semibold ${statusBadgeClass(row.invoiceStatusId)}`}
          >
            {invoiceStatusLabel(row.invoiceStatusId)}
          </span>
        ),
      },
      {
        label: 'Total Incl',
        key: 'totalIncl',
        format: (_value: any, row: InvoiceDTO) =>
          `${row.currency || ''} ${row.totalIncl?.toFixed(2) ?? ''}`,
      },
      {
        label: 'Issue Date',
        key: 'issueDate',
        format: (value: Date) => convertToUserTime(value?.toString() ?? ''),
      },
      {
        label: 'Paid Date',
        key: 'paidDate',
        format: (value: Date) =>
          value ? convertToUserTime(value.toString()) : '',
      },
    ]}
    data={data}
    actions={{
      extra: (row: InvoiceDTO) => {
        const items = [];

        if (row.invoiceStatusId === InvoiceStatusConstants.CREATED_ID) {
          items.push({
            label: 'Mark as Paid',
            onClick: handleMarkAsPaid,
            confirmMessage:
              'Mark this invoice as paid? This will assign an invoice number and email the member.',
            icon: <CheckCircle className="w-4 h-4" />,
          });
        }

        if (row.stripeSubscriptionId) {
          items.push({
            label: 'View Subscription in Stripe',
            onClick: () =>
              window.open(
                `https://dashboard.stripe.com/subscriptions/${row.stripeSubscriptionId}`,
                '_blank',
                'noopener,noreferrer',
              ),
            icon: <ExternalLink className="w-4 h-4" />,
          });
        }

        if (row.stripeCheckoutSessionId) {
          items.push({
            label: 'View Checkout Session in Stripe',
            onClick: () =>
              window.open(
                `https://dashboard.stripe.com/checkout/sessions/${row.stripeCheckoutSessionId}`,
                '_blank',
                'noopener,noreferrer',
              ),
            icon: <ExternalLink className="w-4 h-4" />,
          });
        }

        return items;
      },
    }}
    currentPage={currentPage}
    totalPages={totalPages}
    onPageChange={setCurrentPage}
    pageSize={pageSize}
    onPageSizeChange={setPageSize}
    isLoading={isLoading}
    noDataMessage="No invoices yet"
  />
);

export default InvoiceTable;
